import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';

import { GET_SURVEYS, GET_SURVEY_COUNTS } from '../../../apollo/queries/survey';
import { SurveyRow } from '../types';
import {
    getSurveyStatus,
    mapDisplayStatusToBackend,
    VALID_BACKEND_SURVEY_STATUSES,
    getSurveyDivisionalManagerProgress,
    getSurveyScopeLabel,
    getSurveyScopeStores,
    isStoreScopedSurvey,
    isSurveyInDivisionalManagerReview,
} from '../helpers';
import {
    PriceSurvey,
    SurveyCountsFilterInput,
    SurveyCountsResponse,
    SurveyType,
} from '../../../codegen/graphql';

type UsePriceSurveysDataParams = {
    limit?: number;
    offset?: number;
    statuses?: string[];
    search?: string;
    advertisingZoneIds?: string[];
    storeIds?: string[];
    surveyTypes?: SurveyType[];
    label?: string;
    createdAtStart?: Date | null;
    createdAtEnd?: Date | null;
};

export const usePriceSurveysData = (params: UsePriceSurveysDataParams = {}) => {
    const {
        limit = 20,
        offset = 0,
        statuses,
        search,
        advertisingZoneIds,
        storeIds,
        surveyTypes,
        label,
        createdAtStart,
        createdAtEnd,
    } = params;

    const surveyTypeFilter =
        surveyTypes && surveyTypes.length > 0 ? surveyTypes : undefined;
    const advertisingZoneIdFilter =
        advertisingZoneIds && advertisingZoneIds.length > 0
            ? advertisingZoneIds
            : undefined;
    const storeIdFilter =
        storeIds && storeIds.length > 0 ? storeIds : undefined;

    const backendStatuses = statuses
        ?.map(mapDisplayStatusToBackend)
        ?.filter(
            (s): s is string =>
                s != null && VALID_BACKEND_SURVEY_STATUSES.includes(s),
        );

    const {
        data: surveysData,
        loading: loadingSurveys,
        refetch: refetchSurveys,
        networkStatus: surveysNetworkStatus,
        error: surveysError,
    } = useQuery(GET_SURVEYS, {
        variables: {
            filter: {
                limit,
                offset,
                statuses: backendStatuses,
                search,
                advertisingZoneIds: advertisingZoneIdFilter,
                storeIds: storeIdFilter,
                ...(surveyTypeFilter && { surveyTypes: surveyTypeFilter }),
                ...(label && { label }),
                ...(createdAtStart && { createdAtStart }),
                ...(createdAtEnd && { createdAtEnd }),
            },
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
    });

    const {
        data: countsData,
        loading: loadingCounts,
        refetch: refetchCounts,
        networkStatus: countsNetworkStatus,
        error: countsError,
    } = useQuery(GET_SURVEY_COUNTS, {
        variables: {
            filter: {
                advertisingZoneIds: advertisingZoneIdFilter,
                storeIds: storeIdFilter,
                ...(surveyTypeFilter && { surveyTypes: surveyTypeFilter }),
                ...(createdAtStart && { createdAtStart }),
                ...(createdAtEnd && { createdAtEnd }),
            } satisfies SurveyCountsFilterInput,
        },
        notifyOnNetworkStatusChange: true,
    });

    const isRefetching =
        surveysNetworkStatus === 4 || countsNetworkStatus === 4;
    const isInitialLoading =
        (loadingSurveys && !surveysData) || (loadingCounts && !countsData);
    const isTableLoading = loadingSurveys || loadingCounts || isRefetching;

    const tableData: SurveyRow[] = useMemo(() => {
        const surveys = surveysData?.getSurveys?.surveys || [];
        return surveys.map((survey: PriceSurvey) => {
            const totalItems = survey.totalItemsCount || 0;
            const completedItems = survey.completedItemsCount || 0;
            const championProgress =
                totalItems > 0
                    ? Math.round((completedItems / totalItems) * 100)
                    : 0;

            const isDivisionalManagerReview =
                isSurveyInDivisionalManagerReview(survey);
            const { divisionalManagerPercent } =
                getSurveyDivisionalManagerProgress(survey);

            const progress = isDivisionalManagerReview
                ? divisionalManagerPercent
                : championProgress;
            const progressText = `${progress}% completed`;

            const storesList = getSurveyScopeStores(survey);
            const storeCount = storesList.length;
            const priceChangeRequestsCount =
                survey.priceChangeRequestsCount ?? 0;

            return {
                id: survey.id,
                adZone: getSurveyScopeLabel(survey),
                isStoreScoped: isStoreScopedSurvey(survey),
                surveyType: (survey as { surveyType?: string }).surveyType,
                label: (survey as { label?: string }).label,
                stores: `${storeCount} Store${storeCount !== 1 ? 's' : ''}`,
                storesList,
                championStoreId: survey.championUser?.managerStore?.id ?? null,
                dueDate: dayjs(survey.dueBy).format('YYYY-MM-DD'),
                status: getSurveyStatus(survey.status, survey.dueBy),
                backendStatus: survey.status ?? undefined,
                surveyPeriod: `${dayjs(survey.createdAt).format(
                    'YYYY-MM-DD',
                )} - ${dayjs(survey.dueBy).format('YYYY-MM-DD')}`,
                priceChange: 'N/A',
                priceChangeRequestsCount,
                progress,
                progressText,
                lastActivity: dayjs(survey.updatedAt).format(
                    'YYYY-MM-DD HH:mm',
                ),
            };
        });
    }, [surveysData]);

    const pagination = surveysData?.getSurveys?.pagination;
    const counts: SurveyCountsResponse | undefined =
        countsData?.getSurveyCounts ?? undefined;

    const refetch = async () => {
        await Promise.all([refetchSurveys(), refetchCounts()]);
    };

    return {
        tableData,
        pagination: {
            total: pagination?.total ?? 0,
            limit: pagination?.limit ?? limit,
            offset: pagination?.offset ?? offset,
        },
        counts,
        isInitialLoading,
        isTableLoading,
        refetch,
        error: surveysError || countsError,
    };
};
